import React, { useState, useEffect } from 'react';
import Sidebar from '../components/Sidebar';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Switch } from '../components/ui/switch';
import { Badge } from '../components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { Textarea } from '../components/ui/textarea';
import { 
  Mail, Plus, Edit2, Trash2, Play, Pause, Settings, Zap, Users, 
  FileText, AlertCircle, CheckCircle, Clock, Package, Headphones,
  Cake, Palmtree, Boxes, Receipt, MessageSquare, Filter,
  RefreshCw, Download, Eye, X, LayoutGrid, List, Table2
} from 'lucide-react';
import { toast } from 'sonner';

const API = (process.env.REACT_APP_BACKEND_URL || "");

const iconMap = {
  Headphones: Headphones,
  Package: Package,
  FileCheck: CheckCircle,
  Clock: Clock,
  Cake: Cake,
  Palmtree: Palmtree,
  Users: Users,
  Settings: Settings,
  Boxes: Boxes,
  Receipt: Receipt,
  MessageSquare: MessageSquare,
};

export default function ReglasEmailPage() {
  const [reglas, setReglas] = useState([]);
  const [eventos, setEventos] = useState([]);
  const [tiposDestinatario, setTiposDestinatario] = useState([]);
  const [categorias, setCategorias] = useState([]);
  const [plantillas, setPlantillas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState(null);
  
  // View mode
  const [viewMode, setViewMode] = useState('cards'); // 'cards', 'list', 'table'
  
  // Filters
  const [categoriaFilter, setCategoriaFilter] = useState('all');
  const [estadoFilter, setEstadoFilter] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  
  // Modal states
  const [modalOpen, setModalOpen] = useState(false);
  const [editingRegla, setEditingRegla] = useState(null);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [reglaToDelete, setReglaToDelete] = useState(null);
  const [detailModalOpen, setDetailModalOpen] = useState(false);
  const [detailRegla, setDetailRegla] = useState(null);
  
  // Form state
  const [formData, setFormData] = useState({
    nombre: '',
    descripcion: '',
    categoria: '',
    evento_disparador: '',
    plantilla_id: '',
    destinatarios: [],
    activa: true,
    incluir_firmas_digitales: false,
    prioridad: 0
  });
  
  // New destinatario state
  const [newDestinatario, setNewDestinatario] = useState({
    tipo: '',
    valor: '',
    email_especifico: ''
  });

  const token = localStorage.getItem('token');
  const headers = { 
    'Authorization': `Bearer ${token}`,
    'Content-Type': 'application/json'
  };

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [reglasRes, eventosRes, plantillasRes, statsRes] = await Promise.all([
        fetch(`${API}/api/reglas-email`, { headers }),
        fetch(`${API}/api/reglas-email/eventos`, { headers }),
        fetch(`${API}/api/plantillas-correo`, { headers }),
        fetch(`${API}/api/reglas-email/stats/resumen`, { headers })
      ]);
      
      const reglasData = await reglasRes.json();
      const eventosData = await eventosRes.json();
      const plantillasData = await plantillasRes.json();
      const statsData = await statsRes.json();
      
      setReglas(reglasData.reglas || []);
      setEventos(eventosData.eventos || []);
      setTiposDestinatario(eventosData.tipos_destinatario || []);
      setCategorias(eventosData.categorias || []);
      setPlantillas(plantillasData || []);
      setStats(statsData);
    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error('Error al cargar datos');
    } finally {
      setLoading(false);
    }
  };

  const handleInicializar = async () => {
    try {
      const res = await fetch(`${API}/api/reglas-email/inicializar`, {
        method: 'POST',
        headers
      });
      const data = await res.json();
      toast.success(data.message);
      fetchData();
    } catch (error) {
      toast.error('Error al inicializar reglas');
    }
  };

  const handleToggle = async (reglaId) => {
    try {
      await fetch(`${API}/api/reglas-email/${reglaId}/toggle`, {
        method: 'PUT',
        headers
      });
      fetchData();
      toast.success('Estado actualizado');
    } catch (error) {
      toast.error('Error al cambiar estado');
    }
  };

  const handleDelete = async () => {
    if (!reglaToDelete) return;
    try {
      await fetch(`${API}/api/reglas-email/${reglaToDelete.id}`, {
        method: 'DELETE',
        headers
      });
      setDeleteConfirmOpen(false);
      setReglaToDelete(null);
      fetchData();
      toast.success('Regla eliminada');
    } catch (error) {
      toast.error('Error al eliminar');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.nombre || !formData.evento_disparador || !formData.categoria) {
      toast.error('Complete los campos requeridos');
      return;
    }
    
    if (formData.destinatarios.length === 0) {
      toast.error('Agregue al menos un destinatario');
      return;
    }
    
    try {
      const method = editingRegla ? 'PUT' : 'POST';
      const url = editingRegla 
        ? `${API}/api/reglas-email/${editingRegla.id}`
        : `${API}/api/reglas-email`;
      
      const res = await fetch(url, {
        method,
        headers,
        body: JSON.stringify(formData)
      });
      
      if (!res.ok) throw new Error('Error al guardar');
      
      setModalOpen(false);
      resetForm();
      fetchData();
      toast.success(editingRegla ? 'Regla actualizada' : 'Regla creada');
    } catch (error) {
      toast.error('Error al guardar la regla');
    }
  };

  const resetForm = () => {
    setFormData({
      nombre: '',
      descripcion: '',
      categoria: '',
      evento_disparador: '',
      plantilla_id: '',
      destinatarios: [],
      activa: true,
      incluir_firmas_digitales: false,
      prioridad: 0
    });
    setEditingRegla(null);
    setNewDestinatario({ tipo: '', valor: '', email_especifico: '' });
  };

  const openCreateModal = () => {
    resetForm();
    setModalOpen(true);
  };

  const openEditModal = (regla) => {
    setEditingRegla(regla);
    setFormData({
      nombre: regla.nombre || '',
      descripcion: regla.descripcion || '',
      categoria: regla.categoria || '',
      evento_disparador: regla.evento_disparador || '',
      plantilla_id: regla.plantilla_id || '',
      destinatarios: regla.destinatarios || [],
      activa: regla.activa !== false,
      incluir_firmas_digitales: regla.incluir_firmas_digitales || false,
      prioridad: regla.prioridad || 0
    });
    setModalOpen(true);
  };

  const openDeleteConfirm = (regla) => {
    setReglaToDelete(regla);
    setDeleteConfirmOpen(true);
  };

  const openDetailModal = (regla) => {
    setDetailRegla(regla);
    setDetailModalOpen(true);
  };

  const addDestinatario = () => {
    if (!newDestinatario.tipo) {
      toast.error('Seleccione un tipo de destinatario');
      return;
    }
    
    if (newDestinatario.tipo === 'usuario_especifico' && !newDestinatario.email_especifico) {
      toast.error('Ingrese el email del destinatario');
      return;
    }
    
    setFormData(prev => ({
      ...prev,
      destinatarios: [...prev.destinatarios, { ...newDestinatario }]
    }));
    setNewDestinatario({ tipo: '', valor: '', email_especifico: '' });
  };

  const removeDestinatario = (index) => {
    setFormData(prev => ({
      ...prev,
      destinatarios: prev.destinatarios.filter((_, i) => i !== index)
    }));
  };

  const getDestinatarioLabel = (dest) => {
    if (!dest) return 'Desconocido';
    const tipo = tiposDestinatario.find(t => t.id === dest.tipo);
    if (dest.tipo === 'usuario_especifico' && dest.email_especifico) {
      return `${tipo?.nombre || dest.tipo}: ${dest.email_especifico}`;
    }
    return tipo?.nombre || dest.tipo || 'Desconocido';
  };

  const getCategoriaInfo = (catId) => {
    return categorias.find(c => c.id === catId) || { nombre: catId, color: '#64748b', icono: 'Settings' };
  };

  const filteredReglas = reglas.filter(regla => {
    if (categoriaFilter !== 'all' && regla.categoria !== categoriaFilter) return false;
    if (estadoFilter === 'activas' && !regla.activa) return false;
    if (estadoFilter === 'inactivas' && regla.activa) return false;
    if (searchTerm && !regla.nombre.toLowerCase().includes(searchTerm.toLowerCase())) return false;
    return true;
  });

  // Group reglas by categoria
  const reglasPorCategoria = filteredReglas.reduce((acc, regla) => {
    const cat = regla.categoria;
    if (!acc[cat]) acc[cat] = [];
    acc[cat].push(regla);
    return acc;
  }, {});

  if (loading) {
    return (
      <div className="flex min-h-screen bg-slate-50">
        <Sidebar />
        <main className="ml-0 md:ml-64 flex-1 flex items-center justify-center">
          <RefreshCw className="w-8 h-8 animate-spin text-blue-500" />
        </main>
      </div>
    );
  }

  // Render functions for different views
  const renderCardsView = () => (
    <div className="space-y-6">
      {Object.entries(reglasPorCategoria).map(([catId, catReglas]) => {
        const catInfo = getCategoriaInfo(catId);
        const IconComponent = iconMap[catInfo.icono] || Settings;
        
        return (
          <Card key={catId}>
            <CardHeader className="pb-3">
              <div className="flex items-center gap-3">
                <div 
                  className="p-2 rounded-lg"
                  style={{ backgroundColor: `${catInfo.color}20` }}
                >
                  <IconComponent className="w-5 h-5" style={{ color: catInfo.color }} />
                </div>
                <div>
                  <CardTitle className="text-lg">{catInfo.nombre}</CardTitle>
                  <CardDescription>{catReglas.length} regla(s)</CardDescription>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              <div className="divide-y">
                {catReglas.map(regla => (
                  <div 
                    key={regla.id} 
                    className={`py-4 flex items-center justify-between gap-4 ${!regla.activa ? 'opacity-50' : ''}`}
                  >
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <h4 className="font-medium text-slate-900 truncate">{regla.nombre}</h4>
                        {regla.incluir_firmas_digitales && (
                          <Badge variant="outline" className="text-xs">Con firmas</Badge>
                        )}
                      </div>
                      <div className="flex items-center gap-2 mt-1 flex-wrap">
                        <Badge variant="secondary" className="text-xs">
                          <Zap className="w-3 h-3 mr-1" />
                          {regla.evento_nombre}
                        </Badge>
                        {regla.plantilla_nombre && (
                          <Badge variant="outline" className="text-xs">
                            <FileText className="w-3 h-3 mr-1" />
                            {regla.plantilla_nombre}
                          </Badge>
                        )}
                      </div>
                      <p className="text-sm text-slate-500 mt-1">
                        <Users className="w-3 h-3 inline mr-1" />
                        {regla.destinatarios?.map(d => getDestinatarioLabel(d)).join(', ') || 'Sin destinatarios'}
                      </p>
                    </div>
                    
                    <div className="flex items-center gap-2">
                      <Switch
                        checked={regla.activa}
                        onCheckedChange={() => handleToggle(regla.id)}
                      />
                      <Button variant="ghost" size="icon" onClick={() => openDetailModal(regla)}>
                        <Eye className="w-4 h-4" />
                      </Button>
                      <Button variant="ghost" size="icon" onClick={() => openEditModal(regla)}>
                        <Edit2 className="w-4 h-4" />
                      </Button>
                      <Button variant="ghost" size="icon" className="text-red-600 hover:text-red-700" onClick={() => openDeleteConfirm(regla)}>
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        );
      })}
    </div>
  );

  const renderListView = () => (
    <Card>
      <CardContent className="p-0">
        <div className="divide-y">
          {filteredReglas.map(regla => {
            const catInfo = getCategoriaInfo(regla.categoria);
            return (
              <div 
                key={regla.id} 
                className={`p-4 flex items-center justify-between gap-4 hover:bg-slate-50 ${!regla.activa ? 'opacity-50' : ''}`}
              >
                <div className="flex items-center gap-3 flex-1 min-w-0">
                  <div 
                    className="w-2 h-10 rounded-full"
                    style={{ backgroundColor: catInfo.color }}
                  />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <h4 className="font-medium text-slate-900 truncate">{regla.nombre}</h4>
                      <Badge variant="secondary" className="text-xs">{catInfo.nombre}</Badge>
                    </div>
                    <p className="text-sm text-slate-500 truncate">
                      {regla.evento_nombre} → {regla.destinatarios?.map(d => getDestinatarioLabel(d)).join(', ')}
                    </p>
                  </div>
                </div>
                
                <div className="flex items-center gap-2">
                  <Switch checked={regla.activa} onCheckedChange={() => handleToggle(regla.id)} />
                  <Button variant="ghost" size="icon" onClick={() => openEditModal(regla)}>
                    <Edit2 className="w-4 h-4" />
                  </Button>
                  <Button variant="ghost" size="icon" className="text-red-600" onClick={() => openDeleteConfirm(regla)}>
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            );
          })}
        </div>
      </CardContent>
    </Card>
  );

  const renderTableView = () => (
    <Card>
      <CardContent className="p-0 overflow-x-auto">
        <table className="w-full">
          <thead>
            <tr className="border-b bg-slate-50">
              <th className="text-left p-3 font-medium text-slate-600">Nombre</th>
              <th className="text-left p-3 font-medium text-slate-600">Categoría</th>
              <th className="text-left p-3 font-medium text-slate-600">Evento</th>
              <th className="text-left p-3 font-medium text-slate-600">Destinatarios</th>
              <th className="text-left p-3 font-medium text-slate-600">Plantilla</th>
              <th className="text-center p-3 font-medium text-slate-600">Estado</th>
              <th className="text-center p-3 font-medium text-slate-600">Acciones</th>
            </tr>
          </thead>
          <tbody>
            {filteredReglas.map(regla => {
              const catInfo = getCategoriaInfo(regla.categoria);
              return (
                <tr key={regla.id} className={`border-b hover:bg-slate-50 ${!regla.activa ? 'opacity-50' : ''}`}>
                  <td className="p-3">
                    <span className="font-medium">{regla.nombre}</span>
                    {regla.incluir_firmas_digitales && (
                      <Badge variant="outline" className="ml-2 text-xs">Firmas</Badge>
                    )}
                  </td>
                  <td className="p-3">
                    <Badge style={{ backgroundColor: `${catInfo.color}20`, color: catInfo.color }}>
                      {catInfo.nombre}
                    </Badge>
                  </td>
                  <td className="p-3 text-sm text-slate-600">{regla.evento_nombre}</td>
                  <td className="p-3 text-sm text-slate-600 max-w-xs truncate">
                    {regla.destinatarios?.map(d => getDestinatarioLabel(d)).join(', ') || '-'}
                  </td>
                  <td className="p-3 text-sm text-slate-600">{regla.plantilla_nombre || '-'}</td>
                  <td className="p-3 text-center">
                    <Switch checked={regla.activa} onCheckedChange={() => handleToggle(regla.id)} />
                  </td>
                  <td className="p-3">
                    <div className="flex justify-center gap-1">
                      <Button variant="ghost" size="icon" onClick={() => openEditModal(regla)}>
                        <Edit2 className="w-4 h-4" />
                      </Button>
                      <Button variant="ghost" size="icon" className="text-red-600" onClick={() => openDeleteConfirm(regla)}>
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </CardContent>
    </Card>
  );

  return (
    <div className="flex min-h-screen bg-slate-50">
      <Sidebar />
      <main className="ml-0 md:ml-64 flex-1 p-6 overflow-auto" data-testid="reglas-email-page">
        <div className="max-w-7xl mx-auto space-y-6">
          {/* Header */}
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
            <div>
              <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
                <Mail className="w-7 h-7 text-blue-600" />
                Reglas de Email
              </h1>
              <p className="text-slate-600 mt-1">
                Configure qué emails se envían, cuándo y a quién
              </p>
            </div>
            
            <div className="flex gap-2">
              {reglas.length === 0 && (
                <Button onClick={handleInicializar} variant="outline" className="gap-2">
                  <Download className="w-4 h-4" />
                  Cargar Reglas Predeterminadas
                </Button>
              )}
              <Button onClick={openCreateModal} className="gap-2" data-testid="new-rule-btn">
                <Plus className="w-4 h-4" />
                Nueva Regla
              </Button>
            </div>
          </div>

      {/* Stats Cards */}
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-blue-100 rounded-lg">
                  <Mail className="w-5 h-5 text-blue-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold">{stats.total}</p>
                  <p className="text-sm text-slate-500">Total Reglas</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-green-100 rounded-lg">
                  <Play className="w-5 h-5 text-green-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold">{stats.activas}</p>
                  <p className="text-sm text-slate-500">Activas</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-orange-100 rounded-lg">
                  <Pause className="w-5 h-5 text-orange-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold">{stats.inactivas}</p>
                  <p className="text-sm text-slate-500">Inactivas</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-purple-100 rounded-lg">
                  <Zap className="w-5 h-5 text-purple-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold">{eventos.length}</p>
                  <p className="text-sm text-slate-500">Eventos Disponibles</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Filters and View Toggle */}
      <Card>
        <CardContent className="p-4">
          <div className="flex flex-wrap gap-4 items-center justify-between">
            <div className="flex flex-wrap gap-4 items-center">
              <div className="flex items-center gap-2">
                <Filter className="w-4 h-4 text-slate-400" />
                <span className="text-sm font-medium">Filtros:</span>
              </div>
              
              <Input
                placeholder="Buscar por nombre..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-64"
              />
              
              <Select value={categoriaFilter} onValueChange={setCategoriaFilter}>
                <SelectTrigger className="w-48">
                  <SelectValue placeholder="Categoría" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todas las categorías</SelectItem>
                  {categorias.map(cat => (
                    <SelectItem key={cat.id} value={cat.id}>{cat.nombre}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              
              <Select value={estadoFilter} onValueChange={setEstadoFilter}>
                <SelectTrigger className="w-40">
                  <SelectValue placeholder="Estado" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todos</SelectItem>
                  <SelectItem value="activas">Activas</SelectItem>
                  <SelectItem value="inactivas">Inactivas</SelectItem>
                </SelectContent>
              </Select>
              
              <Button variant="ghost" size="sm" onClick={fetchData}>
                <RefreshCw className="w-4 h-4" />
              </Button>
            </div>
            
            {/* View Toggle */}
            <div className="flex items-center gap-1 bg-slate-100 p-1 rounded-lg">
              <Button
                variant={viewMode === 'cards' ? 'default' : 'ghost'}
                size="sm"
                onClick={() => setViewMode('cards')}
                className="gap-1"
              >
                <LayoutGrid className="w-4 h-4" />
                <span className="hidden sm:inline">Tarjetas</span>
              </Button>
              <Button
                variant={viewMode === 'list' ? 'default' : 'ghost'}
                size="sm"
                onClick={() => setViewMode('list')}
                className="gap-1"
              >
                <List className="w-4 h-4" />
                <span className="hidden sm:inline">Lista</span>
              </Button>
              <Button
                variant={viewMode === 'table' ? 'default' : 'ghost'}
                size="sm"
                onClick={() => setViewMode('table')}
                className="gap-1"
              >
                <Table2 className="w-4 h-4" />
                <span className="hidden sm:inline">Tabla</span>
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Content */}
      {filteredReglas.length === 0 ? (
        <Card>
          <CardContent className="p-12 text-center">
            <Mail className="w-12 h-12 text-slate-300 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-slate-700">No hay reglas configuradas</h3>
            <p className="text-slate-500 mt-1">
              {reglas.length === 0 
                ? 'Haga clic en "Cargar Reglas Predeterminadas" para comenzar'
                : 'No se encontraron reglas con los filtros seleccionados'}
            </p>
          </CardContent>
        </Card>
      ) : (
        <>
          {viewMode === 'cards' && renderCardsView()}
          {viewMode === 'list' && renderListView()}
          {viewMode === 'table' && renderTableView()}
        </>
      )}

      {/* Create/Edit Modal */}
      <Dialog open={modalOpen} onOpenChange={setModalOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editingRegla ? 'Editar Regla' : 'Nueva Regla de Email'}</DialogTitle>
            <DialogDescription>
              Configure cuándo y a quién enviar notificaciones por email
            </DialogDescription>
          </DialogHeader>
          
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="col-span-2">
                <Label>Nombre de la Regla *</Label>
                <Input
                  value={formData.nombre}
                  onChange={(e) => setFormData({ ...formData, nombre: e.target.value })}
                  placeholder="Ej: Notificar ticket asignado"
                  required
                />
              </div>
              
              <div className="col-span-2">
                <Label>Descripción</Label>
                <Textarea
                  value={formData.descripcion}
                  onChange={(e) => setFormData({ ...formData, descripcion: e.target.value })}
                  placeholder="Descripción de lo que hace esta regla..."
                  rows={2}
                />
              </div>
              
              <div>
                <Label>Categoría *</Label>
                <Select 
                  value={formData.categoria} 
                  onValueChange={(v) => setFormData({ ...formData, categoria: v, evento_disparador: '' })}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Seleccione categoría" />
                  </SelectTrigger>
                  <SelectContent>
                    {categorias.map(cat => (
                      <SelectItem key={cat.id} value={cat.id}>{cat.nombre}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              <div>
                <Label>Evento Disparador *</Label>
                <Select 
                  value={formData.evento_disparador} 
                  onValueChange={(v) => setFormData({ ...formData, evento_disparador: v })}
                  disabled={!formData.categoria}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Seleccione evento" />
                  </SelectTrigger>
                  <SelectContent>
                    {eventos
                      .filter(e => !formData.categoria || e.categoria === formData.categoria)
                      .map(evento => (
                        <SelectItem key={evento.id} value={evento.id}>
                          {evento.nombre}
                        </SelectItem>
                      ))}
                  </SelectContent>
                </Select>
              </div>
              
              <div className="col-span-2">
                <Label>Plantilla de Correo (opcional)</Label>
                <Select 
                  value={formData.plantilla_id || "none"} 
                  onValueChange={(v) => setFormData({ ...formData, plantilla_id: v === "none" ? "" : v })}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Seleccione plantilla o use email por defecto" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">Sin plantilla (email generado automáticamente)</SelectItem>
                    {plantillas.map(p => (
                      <SelectItem key={p.id} value={p.id}>{p.nombre}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              {/* Destinatarios */}
              <div className="col-span-2 space-y-3">
                <Label>Destinatarios *</Label>
                
                <div className="flex gap-2">
                  <Select 
                    value={newDestinatario.tipo || "none"} 
                    onValueChange={(v) => setNewDestinatario({ ...newDestinatario, tipo: v === "none" ? "" : v })}
                  >
                    <SelectTrigger className="flex-1">
                      <SelectValue placeholder="Tipo de destinatario" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">Seleccione tipo...</SelectItem>
                      {tiposDestinatario.map(tipo => (
                        <SelectItem key={tipo.id} value={tipo.id}>{tipo.nombre}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  
                  {newDestinatario.tipo === 'usuario_especifico' && (
                    <Input
                      value={newDestinatario.email_especifico}
                      onChange={(e) => setNewDestinatario({ ...newDestinatario, email_especifico: e.target.value })}
                      placeholder="Email del destinatario"
                      type="email"
                      className="flex-1"
                    />
                  )}
                  
                  <Button type="button" onClick={addDestinatario} variant="outline">
                    <Plus className="w-4 h-4" />
                  </Button>
                </div>
                
                {formData.destinatarios.length > 0 && (
                  <div className="flex flex-wrap gap-2 p-3 bg-slate-50 rounded-lg">
                    {formData.destinatarios.map((dest, idx) => (
                      <Badge key={idx} variant="secondary" className="gap-1 py-1 px-2">
                        {getDestinatarioLabel(dest)}
                        <X 
                          className="w-3 h-3 cursor-pointer hover:text-red-600" 
                          onClick={() => removeDestinatario(idx)}
                        />
                      </Badge>
                    ))}
                  </div>
                )}
              </div>
              
              <div className="col-span-2 flex items-center gap-6">
                <div className="flex items-center gap-2">
                  <Switch
                    id="activa"
                    checked={formData.activa}
                    onCheckedChange={(checked) => setFormData({ ...formData, activa: checked })}
                  />
                  <Label htmlFor="activa">Regla Activa</Label>
                </div>
                
                <div className="flex items-center gap-2">
                  <Switch
                    id="firmas"
                    checked={formData.incluir_firmas_digitales}
                    onCheckedChange={(checked) => setFormData({ ...formData, incluir_firmas_digitales: checked })}
                  />
                  <Label htmlFor="firmas">Incluir Firmas Digitales</Label>
                </div>
              </div>
            </div>
            
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setModalOpen(false)}>
                Cancelar
              </Button>
              <Button type="submit">
                {editingRegla ? 'Guardar Cambios' : 'Crear Regla'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Detail Modal */}
      <Dialog open={detailModalOpen} onOpenChange={setDetailModalOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{detailRegla?.nombre}</DialogTitle>
          </DialogHeader>
          {detailRegla && (
            <div className="space-y-4">
              <div>
                <Label className="text-slate-500">Descripción</Label>
                <p>{detailRegla.descripcion || 'Sin descripción'}</p>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-slate-500">Categoría</Label>
                  <p>{getCategoriaInfo(detailRegla.categoria).nombre}</p>
                </div>
                <div>
                  <Label className="text-slate-500">Evento Disparador</Label>
                  <p>{detailRegla.evento_nombre}</p>
                </div>
              </div>
              <div>
                <Label className="text-slate-500">Plantilla</Label>
                <p>{detailRegla.plantilla_nombre || 'Email generado automáticamente'}</p>
              </div>
              <div>
                <Label className="text-slate-500">Destinatarios</Label>
                <div className="flex flex-wrap gap-2 mt-1">
                  {detailRegla.destinatarios?.map((d, i) => (
                    <Badge key={i} variant="secondary">{getDestinatarioLabel(d)}</Badge>
                  ))}
                </div>
              </div>
              <div className="flex gap-4">
                <div className="flex items-center gap-2">
                  {detailRegla.activa ? (
                    <CheckCircle className="w-4 h-4 text-green-600" />
                  ) : (
                    <AlertCircle className="w-4 h-4 text-orange-600" />
                  )}
                  <span>{detailRegla.activa ? 'Activa' : 'Inactiva'}</span>
                </div>
                {detailRegla.incluir_firmas_digitales && (
                  <div className="flex items-center gap-2">
                    <FileText className="w-4 h-4 text-blue-600" />
                    <span>Con firmas digitales</span>
                  </div>
                )}
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation */}
      <Dialog open={deleteConfirmOpen} onOpenChange={setDeleteConfirmOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Confirmar Eliminación</DialogTitle>
            <DialogDescription>
              ¿Está seguro de eliminar la regla "{reglaToDelete?.nombre}"? Esta acción no se puede deshacer.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteConfirmOpen(false)}>
              Cancelar
            </Button>
            <Button variant="destructive" onClick={handleDelete}>
              Eliminar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
        </div>
      </main>
    </div>
  );
}
